import {
    Autocomplete,
    Checkbox,
    Chip,
    Input,
    Slider,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import useSearchLocations from "hooks/api/useSearchLocations";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import debounce from "lodash/debounce";

export default function LocationSearchOnline({ formData, handleSubmit }) {
    const { control, getValues, resetCount } = formData;
    const {
        locations,
        isSearchLocationsLoading,
        fetchLocations,
        setLocations,
    } = useSearchLocations();

    const [locationChoices, setLocationChoices] = useState(() =>
        getValues("location").map((choice) => JSON.parse(choice)),
    );
    const [checkedRadius, setCheckedRadius] = useState(() =>
        Boolean(getValues("locationRadius")),
    );
    const [value, setValue] = useState(() => getValues("locationRadius") || 40);
    const [inputValue, setInputValue] = useState("");
    const [maxLocationErrorMessage, setMaxLocationErrorMessage] =
        useState(false);

    // "Clear all" only resets the form, the chips and radius live here
    useEffect(() => {
        if (resetCount === 0) return;
        setLocationChoices([]);
        setCheckedRadius(false);
        setValue(40);
        setInputValue("");
    }, [resetCount]);

    // Debounced form submission
    const debouncedSubmit = debounce(() => {
        formData.setValue("locationRadius", checkedRadius ? value : null);
        formData.setValue(
            "location",
            locationChoices.map((choice) => JSON.stringify(choice)),
        );
        handleSubmit({
            preventDefault: () => {},
            target: document.activeElement,
        });
    }, 1000);

    useEffect(() => {
        if (locationChoices.length === 0) {
            setCheckedRadius(false);
        }

        if (value < 1) {
            setValue(1);
        }

        if (value > 200) {
            setValue(200);
        }

        debouncedSubmit();
        return () => debouncedSubmit.cancel();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        if (locationChoices.length === 0) {
            setCheckedRadius(false);
        }

        if (locationChoices.length < 10) {
            setMaxLocationErrorMessage(false);
        }

        formData.setValue("locationRadius", checkedRadius ? value : null);
        formData.setValue(
            "location",
            locationChoices.map((choice) => JSON.stringify(choice)),
        );
        handleSubmit({
            preventDefault: () => {},
            target: document.activeElement,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationChoices, checkedRadius]);

    const handleDelete = (choiceToDelete) => {
        setLocationChoices((prev) =>
            prev.filter((choice) => choice.id !== choiceToDelete.id),
        );
    };

    const handleBlur = () => {
        if (value < 1) {
            setValue(1);
        }
        if (value > 200) {
            setValue(200);
        }
    };

    const handleSettingChips = (newValue) => {
        if (newValue) {
            const exists = locationChoices.some(
                (choice) => choice.id === newValue.id,
            );
            if (!exists) {
                if (locationChoices.length >= 10) {
                    setMaxLocationErrorMessage(true);
                    return;
                }
                setInputValue("");
                setLocationChoices((prev) => [...prev, newValue]);
                setLocations([]);
            }
        }
    };

    const handleSliderChange = (_, newValue) => {
        setValue(newValue);
    };

    const handleInputChange = (event) => {
        setValue(event.target.value === "" ? 0 : Number(event.target.value));
    };

    const handleLocationInputChange = (_, value, reason) => {
        if (reason === "clear") {
            setInputValue("");
            setLocations([]);
        }
        if (reason === "input") {
            setInputValue(value);
            fetchLocations(value);
        }
    };

    return (
        <>
            <Typography fontWeight="500">Location</Typography>
            <Stack spacing={1}>
                <Controller
                    name="location"
                    control={control}
                    render={({ field }) => (
                        <>
                            {maxLocationErrorMessage && (
                                <Typography color="error" variant="caption">
                                    You can only select up to 10 locations.
                                </Typography>
                            )}
                            {locationChoices.length > 0 && (
                                <Stack
                                    direction="column"
                                    spacing={1}
                                    sx={{ mt: 2, mb: 1 }}
                                >
                                    {locationChoices.length > 0 &&
                                        locationChoices.map((choice, index) => (
                                            <Chip
                                                title={choice?.title}
                                                key={choice.id || index}
                                                label={choice?.title}
                                                onDelete={() =>
                                                    handleDelete(choice)
                                                }
                                                sx={{
                                                    width: "fit-content",
                                                    maxWidth: "100%",
                                                }}
                                            />
                                        ))}
                                    {checkedRadius && (
                                        <Chip
                                            title={`< ${value} km`}
                                            label={`< ${value} km`}
                                            onDelete={() =>
                                                setCheckedRadius(false)
                                            }
                                            sx={{
                                                width: "fit-content",
                                                maxWidth: "100%",
                                            }}
                                        />
                                    )}
                                </Stack>
                            )}
                            <Autocomplete
                                filterOptions={(x) => x}
                                id="location"
                                fullWidth
                                autoComplete
                                loading={isSearchLocationsLoading}
                                noOptionsText="No locations found"
                                options={locations}
                                getOptionLabel={(option) =>
                                    typeof option === "string"
                                        ? option
                                        : (option?.title ?? "")
                                }
                                isOptionEqualToValue={(option, value) =>
                                    typeof option === "string"
                                        ? option === value
                                        : option?.id === value?.id
                                }
                                onInputChange={handleLocationInputChange}
                                inputValue={inputValue || ""}
                                onChange={(_, newValue) => {
                                    handleSettingChips(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        label="Type your location"
                                    />
                                )}
                            />
                            <Stack sx={{ pt: 1 }}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    onClick={() => {
                                        if (locationChoices.length > 0)
                                            setCheckedRadius(!checkedRadius);
                                    }}
                                    gap={1}
                                >
                                    <Checkbox
                                        checked={checkedRadius}
                                        disabled={locationChoices.length === 0}
                                        value={checkedRadius}
                                        sx={{ p: 0 }}
                                        inputProps={{
                                            "aria-label": "controlled",
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            cursor:
                                                locationChoices.length === 0
                                                    ? "default"
                                                    : "pointer",
                                            color:
                                                locationChoices.length === 0
                                                    ? "text.disabled"
                                                    : "text.primary",
                                        }}
                                    >
                                        Set distance radius (km)
                                    </Typography>
                                </Stack>
                                {checkedRadius && (
                                    <Stack
                                        direction="row"
                                        gap={2}
                                        sx={{ mt: 1 }}
                                    >
                                        <Slider
                                            min={1}
                                            max={200}
                                            step={1}
                                            value={
                                                typeof value === "number"
                                                    ? value
                                                    : 40
                                            }
                                            onChange={handleSliderChange}
                                            aria-labelledby="input-slider"
                                        />
                                        <Input
                                            value={value}
                                            size="small"
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            inputProps={{
                                                step: 1,
                                                min: 1,
                                                max: 200,
                                                type: "number",
                                                "aria-labelledby":
                                                    "input-slider",
                                            }}
                                            sx={{ width: "60px" }}
                                        />
                                    </Stack>
                                )}
                            </Stack>
                        </>
                    )}
                />
            </Stack>
        </>
    );
}
